import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SuperAdminGuard } from '@/components/guards/permission-guards';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useRbac } from '@/hooks/use-rbac';
import { RoleFormData } from '@/types/sheets';

import RolePermissions from './shared/RolePermissions';

interface RoleCreateSheetProps {
  onRoleCreated?: () => Promise<void>;
}

const RoleCreateSheet: React.FC<RoleCreateSheetProps> = ({ onRoleCreated }) => {
  const { hideSheet } = useGlobalState();
  const { createRole, loading } = useRbac();

  const { handleSubmit, control, reset } = useForm<RoleFormData>({
    defaultValues: {
      name: '',
      description: '',
      permissions: {
        dashboard: ['read'],
        notifications: ['read', 'write'],
        users: ['read'],
        audit: ['read'],
      },
    },
  });

  const formatPermissionsForSubmission = (permissions: {
    [resource: string]: string[];
  }) => {
    return Object.entries(permissions).map(([resource, actions]) => ({
      resource,
      actions,
    }));
  };

  const handleCreateRole = handleSubmit(async (formData: RoleFormData) => {
    const hasPermissions = Object.keys(formData.permissions).length > 0;

    if (!hasPermissions) {
      return;
    }

    const formattedPermissions = formatPermissionsForSubmission(
      formData.permissions
    );

    // Sanitize role name by removing spaces
    const sanitizedName = (formData.name || '').replace(/\s+/g, '');

    const success = await createRole({
      name: sanitizedName,
      description: formData.description,
      permissions: formattedPermissions,
      isActive: true,
    });

    if (success) {
      reset();
      hideSheet('create-role');
      // Refresh the roles list if callback provided
      if (onRoleCreated) {
        await onRoleCreated();
      }
    }
  });

  const handleCancel = () => {
    reset();
    hideSheet('create-role');
  };

  // Expose handlers for dynamic button system
  React.useEffect(() => {
    (window as any).roleCreateHandlers = {
      handleCreateRole,
      handleCancel,
      isLoading: loading.operations,
    };

    return () => {
      // Cleanup on unmount
      delete (window as any).roleCreateHandlers;
    };
  }, [handleCreateRole, handleCancel, loading.operations]);

  return (
    <SuperAdminGuard>
      <div className='space-y-6'>
        <form onSubmit={handleCreateRole} className='space-y-6'>
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-70 mb-1'>
                Role Name
              </label>
              <Controller
                name='name'
                control={control}
                rules={{ required: 'Role name is required' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Input
                      {...field}
                      onChange={e =>
                        field.onChange(e.target.value.replace(/\s+/g, ''))
                      }
                      placeholder='Enter role name'
                      className={
                        fieldState.error ? 'border-destructive-50' : ''
                      }
                      disabled={loading.operations}
                    />
                    {fieldState.error && (
                      <p className='text-destructive-50 text-sm mt-1'>
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            <div>
              <Label className='block text-sm font-medium text-gray-70 mb-1'>
                Description (Optional)
              </Label>
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder='Enter role description'
                    rows={3}
                    disabled={loading.operations}
                  />
                )}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-70 mb-3'>
                Permissions
              </label>
              <RolePermissions control={control} />
            </div>
          </div>
        </form>
      </div>
    </SuperAdminGuard>
  );
};

export default RoleCreateSheet;
